import { supabase } from './supabase';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

async function authedFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
	const { data: { session } } = await supabase.auth.getSession();
	const res = await fetch(`${API_URL}${path}`, {
		...options,
		headers: {
			'Content-Type': 'application/json',
			...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
			...options.headers,
		},
	});

	if (!res.ok) {
		const body = await res.text();
		throw new Error(`API ${res.status}: ${body}`);
	}

	return res.json();
}

export interface SendMessageResponse {
	status: string;
	direction: string;
	response?: string;
}

/** Send a message to a user.
 *  direction="inbound"  → stored as inbound, runs through LLM, sends LLM reply via WhatsApp.
 *  direction="outbound" → sends message directly to user via WhatsApp.
 */
export async function sendMessage(
	userId: string,
	message: string,
	direction: 'inbound' | 'outbound'
): Promise<SendMessageResponse> {
	return authedFetch<SendMessageResponse>(`/users/${userId}/messages`, {
		method: 'POST',
		body: JSON.stringify({ message, direction }),
	});
}

// === Code Management API ===
export interface CodeCreatePayload {
	code: string;
	status?: string;
	owner_id?: string | null;
	starts_at?: string | null;
	ends_at?: string | null;
	new_users_only?: boolean;
	order_max_total_uses?: number | null;
	order_max_uses_per_user?: number | null;
	order_max_unique_users?: number | null;
	owner_max_total_payout_uses?: number | null;
	owner_max_payout_uses_per_user?: number | null;
	owner_max_unique_users?: number | null;
	redeemer_type?: string;
	redeemer_currency?: string | null;
	redeemer_value?: number | null;
	owner_type?: string;
	owner_currency?: string | null;
	owner_value?: number | null;
	metadata_json?: Record<string, unknown> | null;
}

export interface CodeUpdatePayload {
	status?: string;
	starts_at?: string | null;
	ends_at?: string | null;
	new_users_only?: boolean;
	order_max_total_uses?: number | null;
	order_max_uses_per_user?: number | null;
	order_max_unique_users?: number | null;
	owner_max_total_payout_uses?: number | null;
	owner_max_payout_uses_per_user?: number | null;
	owner_max_unique_users?: number | null;
	redeemer_type?: string;
	redeemer_currency?: string | null;
	redeemer_value?: number | null;
	owner_type?: string;
	owner_currency?: string | null;
	owner_value?: number | null;
	metadata_json?: Record<string, unknown> | null;
}

export async function createCode(payload: CodeCreatePayload): Promise<any> {
	return authedFetch('/codes', { method: 'POST', body: JSON.stringify(payload) });
}

export async function updateCode(codeId: string, payload: CodeUpdatePayload): Promise<any> {
	return authedFetch(`/codes/${codeId}`, { method: 'PATCH', body: JSON.stringify(payload) });
}
